import { defineStore, storeToRefs } from 'pinia'
import { ref, watch } from 'vue'

import { OutOfPageAdType, PageAdType } from '@/modules/adv'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'

export const PRICE_IN_GAM = {
    1: 30,
    7: 40,
    30: 70,
}

export const useAdDisabledStore = defineStore('ad-disabled', () => {
    const appStore = useAppStore()
    const adsStore = useAdsStore()
    const userStore = useUserStore()
    const layoutStore = useLayoutStore()
    const loggerStore = useLoggerStore()

    const { user } = storeToRefs(userStore)
    const { isIframe, isPortal } = storeToRefs(appStore)

    const isAdDisabled = ref(false)
    const remainingDaysWithoutAd = ref(0)

    function checkAdDisabled() {
        const isPortalEnv = !isIframe.value || isPortal.value
        const until = user.value?.adsDisabledUntil
        const timeRemainingMs = isPortalEnv && until ? new Date(until).getTime() - Date.now() : 0
        isAdDisabled.value = timeRemainingMs > 0
        remainingDaysWithoutAd.value = isAdDisabled.value ? Math.ceil(timeRemainingMs / (1000 * 60 * 60 * 24)) : 0
        adsStore.adService?.toggleState(!isAdDisabled.value)
    }

    if (typeof window !== 'undefined') {
        checkAdDisabled()
        setInterval(checkAdDisabled, 60_000)
        watch(() => user.value?.adsDisabledUntil, checkAdDisabled)
    }

    const adDisabledModalOptions = ref({
        isOpened: false,
        isTopUp: false,
        adPlacement: undefined as OutOfPageAdType | PageAdType | undefined,
    })
    const isAdFinalScreenVisible = ref(false)

    function closeAdDisabledModal() {
        adDisabledModalOptions.value = {
            isOpened: false,
            isTopUp: false,
            adPlacement: undefined,
        }
    }

    function closeNoAdFinalScreen() {
        isAdFinalScreenVisible.value = false
    }

    async function openDisabledAdModal(adPlacement?: OutOfPageAdType) {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'open_purchase_window',
            adPlacement,
        })

        try {
            const currentUser = await userStore.authorizeUser()

            if (currentUser) {
                adDisabledModalOptions.value = {
                    isOpened: true,
                    isTopUp: isAdDisabled.value,
                    adPlacement,
                }
            }
        } catch {}
    }

    async function disableAd(durationInDays: 1 | 7 | 30) {
        adDisabledModalOptions.value.isOpened = false

        try {
            await layoutStore.setPaymentModalOrder({
                type: 'disable-ad-order',
                amount: PRICE_IN_GAM[durationInDays],
                durationInDays,
                externalId: `no-ads-${durationInDays}-days-${Date.now()}`,
                adPlacement: adDisabledModalOptions.value.adPlacement,
                isTopUp: adDisabledModalOptions.value.isTopUp,
            })

            isAdFinalScreenVisible.value = true
        } catch {}

        adDisabledModalOptions.value.adPlacement = undefined
    }

    return {
        isAdDisabled,
        remainingDaysWithoutAd,
        disableAd,
        adDisabledModalOptions,
        openDisabledAdModal,
        closeAdDisabledModal,
        isAdFinalScreenVisible,
        closeNoAdFinalScreen,
    }
})
